import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { HOME_HERO_IMAGE, HOME_HERO_IMAGE_MOBILE } from './data';
import { EntranceHeadline } from './entrance_headline';
import {
  HeadingH1,
  HomeDesktopOnly,
  HomeMobileOnly,
  SectionContainer,
} from './styles';
import { MEDIA, TOKENS } from './tokens';
import { useParallax } from './use_parallax';
import { HomeZipBar } from './zip_bar';
import HERO_DESKTOP_MP4 from '@videos/home/hero-desktop.mp4';
import HERO_MOBILE_MP4 from '@videos/home/hero-mobile.mp4';

const HeroSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;

  padding: ${TOKENS.headerOffset} 0 0;

  min-height: 100svh;
  min-height: 100dvh;

  overflow: hidden;
  background: ${TOKENS.color.foreground};
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${TOKENS.color.imageOverlay};
    pointer-events: none;
  }
`;

const heroMediaStyles = `
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 112%;
  object-fit: cover;
  object-position: center bottom;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`;

const HeroVideo = styled.video<{ $parallaxY: number }>`
  ${heroMediaStyles}
  transform: translate3d(0, ${({ $parallaxY }) => `${$parallaxY}%`}, 0);
`;

const HeroVideoMobile = styled(HeroVideo)`
  ${MEDIA.md} {
    display: none;
  }
`;

const HeroVideoDesktop = styled(HeroVideo)`
  display: none;

  ${MEDIA.md} {
    display: block;
  }
`;

const HeroImage = styled.img<{ $parallaxY: number }>`
  ${heroMediaStyles}
  transform: translate3d(0, ${({ $parallaxY }) => `${$parallaxY}%`}, 0);
`;

const HeroImageMobile = styled(HeroImage)`
  ${MEDIA.md} {
    display: none;
  }
`;

const HeroImageDesktop = styled(HeroImage)`
  display: none;

  ${MEDIA.md} {
    display: block;
  }
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: end;
  padding: ${TOKENS.space[64]} ${TOKENS.space[24]};

  ${MEDIA.md} {
    padding-left: ${TOKENS.space[40]};
    padding-right: ${TOKENS.space[40]};
  }

  ${MEDIA.lg} {
    justify-content: flex-end;
    /* Content block → bottom of hero: 80px (Figma) */
    padding-top: 0;
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
    padding-bottom: 80px;
  }
`;

const HeroContentInner = styled(SectionContainer)`
  ${MEDIA.lg} {
    margin-top: auto;
    margin-bottom: 0;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${TOKENS.space[24]};
  max-width: 100%;
  text-align: left;

  ${MEDIA.lg} {
    gap: 0;
    max-width: 580px;
  }

  ${MEDIA.xl} {
    max-width: 720px;
  }

  ${MEDIA.xxl} {
    max-width: 780px;
  }
`;

const HeroTextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[16]};
`;

const ZipFormWrap = styled.div`
  width: 100%;

  ${MEDIA.lg} {
    margin-top: ${TOKENS.space[40]};
  }
`;

const HeroHeadline = styled(HeadingH1)`
  /* Keep Figma mobile line breaks intact below the 375 design frame. */
  @media (max-width: 374px) {
    font-size: 36px;
    line-height: 45px;
  }

  ${MEDIA.lg} {
    font-size: 68px;
    line-height: 80px;
  }

  ${MEDIA.xl} {
    font-size: 68px;
    line-height: 80px;
  }

  ${MEDIA.xxl} {
    font-size: 68px;
    line-height: 80px;
  }
`;

/** One Figma line — block + nowrap so the browser cannot reflow mid-phrase. */
const HeroLine = styled.span`
  display: block;
  white-space: nowrap;
`;

const MobileHeroHeadline = styled.span`
  display: block;

  ${MEDIA.lg} {
    display: none;
  }
`;

const DesktopHeroHeadline = styled.span`
  display: none;

  ${MEDIA.lg} {
    display: inline;
  }
`;

const HomeHeroTrust = styled.p`
  color: ${TOKENS.color.inverse};
  font-family: ${TOKENS.font.sans};
  /* Heading/H3 Mobile — Figma */
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;

  ${MEDIA.lg} {
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
  }
`;

/** Forces a line break only below 375 so “protecting what” still fits on 320. */
const HeroTrustNarrowBreak = styled.span`
  @media (max-width: 374px) {
    display: block;
  }
`;

export const HomeHero: React.FC = () => {
  const parallaxY = useParallax();
  const [preferStatic, setPreferStatic] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPreferStatic(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return (
    <HeroSection>
      <HeroBackground aria-hidden="true">
        {preferStatic ? (
          <>
            <HeroImageMobile
              src={HOME_HERO_IMAGE_MOBILE}
              alt=""
              loading="eager"
              $parallaxY={parallaxY}
            />
            <HeroImageDesktop
              src={HOME_HERO_IMAGE}
              alt=""
              loading="eager"
              $parallaxY={-parallaxY}
            />
          </>
        ) : (
          <>
            <HeroVideoMobile
              autoPlay
              muted
              loop
              playsInline
              poster={HOME_HERO_IMAGE_MOBILE}
              $parallaxY={parallaxY}
            >
              <source src={HERO_MOBILE_MP4} type="video/mp4" />
            </HeroVideoMobile>
            <HeroVideoDesktop
              autoPlay
              muted
              loop
              playsInline
              poster={HOME_HERO_IMAGE}
              $parallaxY={-parallaxY}
            >
              <source src={HERO_DESKTOP_MP4} type="video/mp4" />
            </HeroVideoDesktop>
          </>
        )}
      </HeroBackground>

      <HeroContent>
        <HeroContentInner>
          <HeroCopy>
            <HeroTextStack>
              <EntranceHeadline as={HeroHeadline} immediate slowEnter>
                <MobileHeroHeadline>
                  <HeroLine>Trusted with the</HeroLine>
                  <HeroLine>world&apos;s history...</HeroLine>
                  <HeroLine>and now yours.</HeroLine>
                </MobileHeroHeadline>
                <DesktopHeroHeadline>
                  Trusted with the world&apos;s
                  <br />
                  history... and now yours.
                </DesktopHeroHeadline>
              </EntranceHeadline>
              <HomeHeroTrust>
                <HomeMobileOnly>
                  Backed by Iron Mountain, protecting <HeroTrustNarrowBreak />
                  what
                  <br />
                  matters most since 1951.
                </HomeMobileOnly>
                <HomeDesktopOnly>
                  Backed by Iron Mountain, protecting what matters most since
                  1951.
                </HomeDesktopOnly>
              </HomeHeroTrust>
            </HeroTextStack>
            <ZipFormWrap>
              <HomeZipBar
                id="home_hero_zip"
                container="home_hero"
                cta="Get started"
                placeholder="Enter pick-up ZIP"
                variant="hero"
              />
            </ZipFormWrap>
          </HeroCopy>
        </HeroContentInner>
      </HeroContent>
    </HeroSection>
  );
};
